// The containers spessasynth reads — SF2, SF3 (an sfbk whose samples are Ogg Vorbis,
// ifil major 3) and DLS — told apart from their headers without parsing them. The app
// decides what a dropped file is and what to call a download by this; the worklet
// parses its own copy of the bytes.

export type SoundBankFormat = "sf2" | "sf3" | "dls"

ascii := (bytes: Uint8Array, offset: number, length: number) ->
  String.fromCharCode ...bytes.subarray(offset, offset + length)

/** Visits every sub-chunk of the top-level INFO list of an sfbk or DLS RIFF. */
eachInfoChunk := (buffer: ArrayBuffer, visit: (id: string, offset: number, size: number) => void) ->
  bytes := new Uint8Array buffer
  view := new DataView buffer
  offset .= 12
  while offset + 12 <= bytes.length
    size := view.getUint32 offset + 4, true
    if ascii(bytes, offset, 4) is "LIST" and ascii(bytes, offset + 8, 4) is "INFO"
      sub .= offset + 12
      end := Math.min bytes.length, offset + 8 + size
      while sub + 8 <= end
        subSize := view.getUint32 sub + 4, true
        visit ascii(bytes, sub, 4), sub + 8, subSize
        sub += 8 + subSize + (subSize & 1)
      return
    offset += 8 + size + (size & 1)
  return

/** "sf2", "sf3" or "dls" for a RIFF sound bank, undefined for anything else. Headers only. */
export soundBankFormat := (buffer: ArrayBuffer): SoundBankFormat? ->
  bytes := new Uint8Array buffer
  return unless bytes.length >= 12 and ascii(bytes, 0, 4) is "RIFF"
  switch ascii(bytes, 8, 4)
    when "DLS " then "dls"
    when "sfbk"
      major .= 2
      eachInfoChunk buffer, (id, offset, size) ->
        if id is "ifil" and size >= 4
          major = new DataView(buffer).getUint16 offset, true
      if major >= 3 then "sf3" else "sf2"
    else undefined

/** The INAM of the INFO list — the bank's name, the way SF2Parser reads `name`. */
export readSoundBankName := (buffer: ArrayBuffer): string? ->
  return unless soundBankFormat buffer
  bytes := new Uint8Array buffer
  name: string? .= undefined
  eachInfoChunk buffer, (id, offset, size) ->
    if id is "INAM"
      name ?= ascii(bytes, offset, size).replace /\0+$/, ""
  name

/** A preset as the channel dropdowns list it: bank 128 is the drum kits, as in SF2. */
export type SoundBankPreset = { bank: number, program: number, name: string }

/** Visits the chunks of a RIFF list's body, `start` to `end`. */
eachChunk := (view: DataView, start: number, end: number, visit: (id: string, offset: number, size: number) => void) ->
  bytes := new Uint8Array view.buffer
  offset .= start
  while offset + 8 <= end
    size := view.getUint32 offset + 4, true
    visit ascii(bytes, offset, 4), offset + 8, Math.min(size, end - offset - 8)
    offset += 8 + size + (size & 1)
  return

/** The LIST chunk of `type` among `start`..`end`, as the offset and end of its body. */
findList := (view: DataView, start: number, end: number, type: string) ->
  bytes := new Uint8Array view.buffer
  found: [number, number]? .= undefined
  eachChunk view, start, end, (id, offset, size) ->
    if id is "LIST" and !found and ascii(bytes, offset, 4) is type
      found = [offset + 4, offset + size]
  found

cString := (bytes: Uint8Array, offset: number, length: number) ->
  ascii(bytes, offset, length).replace /\0.*$/s, ""

/**
Every preset a sound bank holds, from its headers: an SF2/SF3's `phdr`, a DLS's `insh`
and `INAM` per instrument. What SpessaSynth's `banks` are built from — the worklet parses
the bank itself, and the main thread only needs names.
*/
export readSoundBankPresets := (buffer: ArrayBuffer): SoundBankPreset[] ->
  format := soundBankFormat buffer
  return [] unless format
  view := new DataView buffer
  bytes := new Uint8Array buffer
  presets: SoundBankPreset[] := []
  if format is "dls"
    lins := findList view, 12, bytes.length, "lins"
    return presets unless lins
    eachChunk view, lins[0], lins[1], (id, offset, size) ->
      return unless id is "LIST" and ascii(bytes, offset, 4) is "ins "
      bank .= 0
      program .= 0
      name .= ""
      eachChunk view, offset + 4, offset + size, (sub, o, s) ->
        if sub is "insh" and s >= 12
          ulBank := view.getUint32 o + 4, true
          bank = if ulBank >>> 31 then 128 else (ulBank >> 8) & 0x7f
          program = view.getUint32(o + 8, true) & 0x7f
        else if sub is "LIST" and ascii(bytes, o, 4) is "INFO"
          eachChunk view, o + 4, o + s, (info, io, isize) ->
            name = cString bytes, io, isize if info is "INAM"
      presets.push { bank, program, name }
  else
    pdta := findList view, 12, bytes.length, "pdta"
    return presets unless pdta
    eachChunk view, pdta[0], pdta[1], (id, offset, size) ->
      return unless id is "phdr"
      // 38-byte records; the last is the terminal "EOP"
      for i of [0...Math.floor(size / 38) - 1]
        r := offset + i * 38
        presets.push
          name: cString bytes, r, 20
          program: view.getUint16 r + 20, true
          bank: view.getUint16 r + 22, true
  presets
